import { BASE_URL } from '../../../constants/index.js';

export const getComments = (id) => {
    return fetch(`${BASE_URL}/quotations/comment/${id}`, {
        credentials: 'include',
        method: 'GET',
    })
    .then(response => response.json());
}

export const getCommentsApproval = (id) => {
    return fetch(`${BASE_URL}/approvals/comments/${id}`, {
        credentials: 'include',
        method: 'GET',
    })
    .then(response => response.json());
}

export const getHistory = (id, { page = 1, limit = 8, sort = 'createdAt', order = 'desc' } = {}) => {
    const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
        sort,
        order,
    });
    return fetch(`${BASE_URL}/quotations/history/${id}?${params.toString()}`, {
        credentials: 'include',
        method: 'GET',
    })
    .then(async (response) => {
        const data = await response.json();
        if (Array.isArray(data)) {
            return { data, total: data.length, page: 1, limit };
        }
        return {
            data: data?.data ?? [],
            total: data?.total ?? 0,
            page: data?.page ?? page,
            limit: data?.limit ?? limit,
        };
    });
};

export const getStatus = (id) => {
    return fetch(`${BASE_URL}/quotations/quoteitems/${id}`, {
        credentials: 'include',
        method: 'GET',
    })
    .then(response => response.json());
}

export const getFiles = (url) => {
    return fetch(`${BASE_URL}${url}`, {
        credentials: 'include',
        method: 'GET',
    })
    .then(response => response.json());
}

/** GET attachments for a quotation key (QID-…). */
export const fetchQuotationAttachments = (quoteKey) => {
    const params = new URLSearchParams({
        pid: 'na',
        category: 'quotation',
        key: quoteKey,
    });
    params.append('fields', 'meta.durl_link');
    params.append('fields', 'meta.vurl_link');
    return getFiles(`/activities/action/attachment?${params.toString()}`);
};

/** Activities list item → QuoteAttachment row (meta.durl_link → murl_link for Download). */
export const mapActivitiesToAttachmentRows = (items) => {
    if (!Array.isArray(items)) return [];
    return items
        .map((item) => {
            const meta = item.meta || {};
            const _id = item._id ?? meta.id;
            if (!_id) return null;
            return {
                _id,
                actionValue: item.actionValue ?? item.file_name ?? 'Attachment',
                meta: {
                    murl_link: meta.durl_link ?? meta.murl_link,
                    vurl_link: meta.vurl_link,
                    createdByUserName: meta.createdByUserName,
                },
                author: meta.createdByUserName,
            };
        })
        .filter(Boolean);
};

export const postComment = (id, newNote) => {
    return fetch(`${BASE_URL}/quotations/comment/${id}`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(newNote),
    })
    .then(response => response.json());
}

export const postCommentApproval = (id, newNote) => {
    return fetch(`${BASE_URL}/approvals/comments/${id}`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(newNote),
    })
    .then(response => response.json());
}

export const postItems = async (id, newItems) => {
    try {
        const response = await fetch(`${BASE_URL}/quotations/items/${id}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({ items: newItems }),
        });

        if (!response.ok) {
            throw new Error('Network response was not ok');
        }

        const data = await response.json();
        return { ...data, ok: true };
    } catch (error) {
        console.error("Error in postItems:", error);
        return { ok: false, error: error.message };
    }
};
export const fetchQuoteWithQuoteId = async (quoteId) => {
    try {
        const response = await fetch(`${BASE_URL}/quotations/${quoteId}`, {
            credentials: 'include',
        });
        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error fetching items:', error);
        return null;
    }
}
export const deleteQuoteRelation = async (relationId) => {
    try {
        const response = await fetch(`${BASE_URL}/quotations/quote-relation/${relationId}`, {
            method: 'DELETE',
            credentials: 'include',
        });
        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error deleting quote relation:', error);
        return null;
    }
}

export const fetchQuotationMetrics = async (orgId, period = 'this_month') => {
    try {
        const response = await fetch(`${BASE_URL}/quotations/metrics/source/${orgId}?period=${period}`, {
            credentials: 'include',
        });
        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error fetching quotation metrics:', error);
        return null;
    }
};

export const performItemAction = async (quoteRelationId, itemId, action, additionalData = {}) => {
    try {
        const response = await fetch(`${BASE_URL}/quotations/items/${quoteRelationId}/action`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({ itemId, action, ...additionalData }),
        });
        const data = await response.json();
        return { ...data, ok: response.ok };
    } catch (error) {
        console.error('Error performing item action:', error);
        return { ok: false, error: error.message };
    }
};

/** POST approval action endpoint (Approve / Partial Approve / Partial Reject / Reject). */
export const postApprovalAction = async (endpoint, body = {}) => {
    try {
        if (!endpoint) {
            return { ok: false, message: 'Approval action is not available' };
        }
        const response = await fetch(`${BASE_URL}${endpoint}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify(body),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            const message =
                (typeof data?.message === 'string' && data.message) ||
                (typeof data?.error === 'string' && data.error) ||
                'Something went wrong';
            return { ok: false, message };
        }
        return { ok: true, data };
    } catch (error) {
        console.error('Error posting approval action:', error);
        return { ok: false, message: error.message || 'Something went wrong' };
    }
};